import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';

import { DialogService } from '../components/dialog/service/dialog.service';
import { SpinnerOverlayService } from '../components/login/spinner/spinner-overlay/spinner-overlay.service';
import { EventManager } from '../service/event-manager.service';
//import { captureException } from '@sentry/angular';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    static readonly REFRESH_PAGE_ON_TOAST_CLICK_MESSAGE: string =
        'An error occurred: Please click this message to refresh';
    static readonly DEFAULT_ERROR_TITLE: string = 'Something went wrong';
    chunkFailedMessage = /Loading chunk [\d]+ failed/;
    errors = [
        'Failed to fetch dynamically imported module',
        'Failed to load module script',
        'Unable to preload CSS',
        'Importing a module script failed',
        'Expected a JavaScript module',
    ];
    constructor(
        private progressService: FuseProgressBarService,
        private _dialogService: DialogService,
        private _eventManager: EventManager,
        private _translate: TranslateService,
        private _overlayService: SpinnerOverlayService
    ) {}

    public handleError(error: Error | HttpErrorResponse | any): void {
        if (error.message && this.chunkFailedMessage.test(error.message)) {
            if (navigator.onLine) {
                window.location.reload();
                return;
            }
        }
        if (error.message && this.errors.some(e => error.message.includes(e))) {
            console.log('ERROR-CHUNK', navigator.onLine, error, error.message, JSON.stringify(error));
            if (navigator.onLine) {
                window.location.reload();
                return;
            }
        }
        if (typeof error == 'string' && this.errors.some(e => error.includes(e))) {
            console.log('ERROR-CHUNK', navigator.onLine, error, JSON.stringify(error));
            if (navigator.onLine) {
                window.location.reload();
                return;
            }
        }
        // console.log('Error service', error);
        // Progress started ? Then hide
        console.warn('Global-Error:', error);
        this._eventManager.broadcast({
            name: 'button-clicked',
            content: { error: error },
        });

        //const httpErrorCode = (<any>error)?.status;
        //ERR_NETWORK_IO_SUSPENDED
        if (error instanceof HttpErrorResponse) {
            if (error.status == 0 && environment.production) {
                window.location.reload();
            }
        }

        if (error instanceof HttpErrorResponse) {
            if (error.error instanceof Blob) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const newError = JSON.parse(reader.result as string);
                    this.showErrorMessage(error.status, newError, error?.url);
                };
                reader.readAsText(error.error);
            } else {
                this.showErrorMessage(error.status, error.error, error?.url);
            }
            //this.traceError(error.error, 'SERVER-SIDE');
        } else {
            if (error.code != 953) {
                this.showError(error.message);
            }
            console.log('OTHER-ERROR', error);
            //this.traceError(error, 'CLIENT-SIDE');
        }
    }

    private showErrorMessage(httpErrorCode: any, error: any, url) {
        console.log('HANDLING-ERROR', error);
        switch (httpErrorCode) {
            case HttpStatusCode.Unauthorized: // StatusCodes.UNAUTHORIZED:
                this._overlayService.hide();
                break;
            case HttpStatusCode.Forbidden: // StatusCodes.FORBIDDEN:
                this.showError(error?.message ? error.message : error.message, url);
                break;
            case HttpStatusCode.BadRequest: // StatusCodes.BAD_REQUEST:
                console.log('BADREQUEST', error);
                this.showError(error?.message ? error.message : error.detail, error.detail ?? url);
                break;
            case HttpStatusCode.BadGateway: // StatusCodes.BAD_GATEWAY:
                this._dialogService.info(this._translate.instant('GENERAL.MESSAGE.ERROR.GATEWAY_TIMEOUT'), {
                    id: 'gateway_error_message',
                    dismissible: false,
                });
                break;
            case HttpStatusCode.ServiceUnavailable: // StatusCodes.BAD_GATEWAY:
                this._dialogService.info(this._translate.instant('GENERAL.MESSAGE.ERROR.GATEWAY_TIMEOUT'), {
                    id: 'gateway_error_message',
                    dismissible: false,
                    autoClose: false,
                });
                break;
            case HttpStatusCode.InternalServerError: // StatusCodes.INTERNAL_SERVER_ERROR:
                if (
                    !error ||
                    (error?.message && (error.message == 'COMMAND_EXCEPTION' || error.message == 'GENERAL'))
                ) {
                    error = { message: this._translate.instant('GENERAL.MESSAGE.ERROR.GATEWAY_TIMEOUT') };
                    this.showError(error.message, url);
                } else {
                    this.showError(error?.detail ? error.detail : error.message, url);
                }
                break;
            case HttpStatusCode.NotFound: // StatusCodes.NOT_FOUND:
                this._dialogService.error(error?.detail ?? error.message, url);
                break;
            case HttpStatusCode.GatewayTimeout: // GATEWAY_TIMEOUT:
                this._dialogService.info(this._translate.instant('GENERAL.MESSAGE.ERROR.GATEWAY_TIMEOUT'), {
                    id: 'gateway_error_message',
                    dismissible: false,
                    autoClose: false,
                });
                break;
            case HttpStatusCode.PayloadTooLarge: // Too large file upload:
                this._dialogService.info(
                    this._translate.instant('ATTACHMENT.MESSAGES.FILE_IS_TOO_LARGE', { item: '1GB' })
                );
                break;
            default:
                this.showError(error?.message ? error.message : error.message ? error.message : error.detail, url);
        }
    }

    private showError(message: any, detail?: string) {
        console.warn('Error-Message : ', message);
        if (message) {
            this._dialogService.error(message, detail);
        }
        this.progressService.hide();
    }

    //traceError(error: any, message: string) {
    /*
        if (navigator.onLine && environment.traceErrorLog) {
            const handledError = error.originalError || error;
            const username = this._principalService.isAuthenticated
                ? this._principalService.authenticatedUser.username
                : 'no-user';
            const email = this._principalService.isAuthenticated
                ? this._principalService.authenticatedUser.email
                : 'no-email';
            captureException(handledError, {
                user: {
                    username: username,
                    email: email,
                },
                level: 'error',
                tags: { type: message, environment: environment.appUrl, url: this._router.url },
                extra: {
                    error: JSON.stringify(handledError),
                },
            });
        }
        */
    //}
}
